import {
  ICombinedSimpleModel,
  ISimpleFilterModel,
  TextFilterModel,
} from '@ag-grid-community/core'
import { splitData } from '@helios/utils'
import { FilterType, MultiFilter, SingleFilter } from './filter'

export interface TextFilter extends MultiFilter<string> {
  filterType: FilterType.Text
  filters: SingleTextFilter[]
}

/*
Either value or values must be present. Value must be present on operations expecting only one value.
And values must be present on operators where multiple values could be present like in and not in.
 */
interface SingleTextFilter extends SingleFilter<string> {
  operator: ServerTextFilterOperator
  value?: string

  // populated if the operator is 'in' or 'notIn'
  values?: string[]
}

type ServerTextFilterOperator =
  | 'contains'
  | 'notContains'
  | 'equals'
  | 'notEqual'
  | 'startsWith'
  | 'endsWith'
  | 'in'
  | 'notIn'
  | 'blank'
  | 'notBlank'

type UITextFilterOperator = ServerTextFilterOperator | 'containsIn'

const UITextFilterOperatorSet: Set<UITextFilterOperator> = new Set([
  'contains',
  'notContains',
  'equals',
  'notEqual',
  'startsWith',
  'endsWith',
  'in',
  'notIn',
  'containsIn',
  'blank',
  'notBlank',
])

/**
 * Check if string is text filter operator
 */
function isTextFilterOperator(s: string): s is UITextFilterOperator {
  return UITextFilterOperatorSet.has(s as UITextFilterOperator)
}

/**
 *
 */
export function convertICombinedSimpleModelToTextFilter(
  combinedFilter: ICombinedSimpleModel<ISimpleFilterModel>,
): TextFilter | null {
  if (
    combinedFilter.condition1.filterType !== FilterType.Text ||
    combinedFilter.condition2.filterType !== FilterType.Text
  ) {
    console.error('Filter type should be text', combinedFilter)
    return null
  }
  const firstFilterModel = combinedFilter.condition1 as TextFilterModel
  const firstSingleFilter = convertSingleFilter(firstFilterModel)

  const secondFilterModel = combinedFilter.condition2 as TextFilterModel
  const secondSingleFilter = convertSingleFilter(secondFilterModel)

  if (firstSingleFilter && secondSingleFilter) {
    return {
      operator: combinedFilter.operator,
      filterType: FilterType.Text,
      filters: [firstSingleFilter, secondSingleFilter],
    }
  } else {
    return null
  }
}

/**
 *
 */
export function convertISimpleFilterModelToTextFilter(
  filter: ISimpleFilterModel,
): TextFilter | null {
  if (filter.filterType !== FilterType.Text) {
    console.error('Filter type should be text', filter)
    return null
  }
  const filterModel = filter as TextFilterModel

  if ((filterModel.type as UITextFilterOperator) === 'containsIn') {
    return convertContainsIn(filterModel)
  }

  const singleFilter = convertSingleFilter(filterModel)
  if (singleFilter) {
    return {
      operator: 'AND',
      filterType: FilterType.Text,
      filters: [singleFilter],
    }
  } else {
    return null
  }
}

/**
 *
 */
function convertContainsIn(filterModel: TextFilterModel): TextFilter | null {
  if (filterModel.filter == null) {
    console.error('Filter value(s) not supported', filterModel)
    return null
  }

  const filters = splitData(filterModel.filter)
    .map(value =>
      convertSingleFilter({
        type: 'contains',
        filter: value,
      }),
    )
    .filter((filter): filter is SingleTextFilter => !!filter?.value)

  return {
    operator: 'OR',
    filterType: FilterType.Text,
    filters,
  }
}

/**
 *
 */
function convertSingleFilter(
  filterModel: TextFilterModel,
): SingleTextFilter | null {
  if (filterModel.type == null || !isTextFilterOperator(filterModel.type)) {
    console.error('Filter operator not supported', filterModel)
    return null
  }
  if (filterModel.filter == null) {
    console.error('Filter value(s) not supported', filterModel)
    return null
  }
  const operator = filterModel.type as ServerTextFilterOperator
  const filter: string = filterModel.filter

  const singleTextFilter: SingleTextFilter = {
    operator,
  }

  if (operator === 'in' || operator === 'notIn')
    singleTextFilter.values = splitData(filter)
  else {
    singleTextFilter.value = filter
  }
  return singleTextFilter
}
